import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  ComponentView,
  ComponentsOverview,
  DeploymentGroup,
  Environment,
  ProjectApplication,
  ProjectDeployment,
} from '../api';
import { useResource } from '../hooks';
import { applicationTypeLabel, cacheProviderLabel, dnsStatusLabel, humanize } from '../labels';
import { ConfirmDialog, ErrorNotice, LoadingRows, StatusBadge } from './ui';
import { ActionMenu, Callout, Icon, Section, Subsection } from './primitives';
import type { MenuAction } from './primitives';
import { ResponsiveTable } from './primitives/ResponsiveTable';
import type { Column } from './primitives/ResponsiveTable';
import { useFrontendFrameworks } from './BuildSettingsFields';
import { AddFrontendModal } from './AddFrontendModal';
import { ComponentSettingsModal } from './ComponentSettingsModal';
import { ComponentHistoryPanel } from './ComponentHistoryPanel';
import { groupHistoryLabel, groupNowValue } from './deploymentGroupState';

interface ComponentsCardProps {
  projectId: string;
  environment: Environment;
  /** The Magento backend must be placed before anything deploys. */
  canDeploy: boolean;
  /** Bumped by the page when a deployment was created elsewhere. */
  reloadToken?: number;
  /** Open the deployment dialog preselecting these components (all when empty). */
  onDeploy: (componentIds: string[]) => void;
  /** Show one deployment's timeline. */
  onOpenDeployment: (deploymentId: string) => void;
  /** Show one coordinated deployment. */
  onOpenGroup: (groupId: string) => void;
  onApplicationsChanged: () => void;
  /** Why a frontend cannot be added yet (for example the Magento setup has
   * not run); adding is allowed when absent (CO-73). */
  addFrontendBlockedReason?: string | null;
  /** Opens the project's repository access settings. */
  onConfigureAccess?: () => void;
  /** Opens the page's own Add frontend dialog; without it the card opens its own. */
  onAddFrontend?: () => void;
  /** A Magento Headless project: its storefronts are frontends, so having
   * none yet is an empty state that offers to add the first one. */
  headless?: boolean;
  /** Where the Magento setup is finished, linked when adding is blocked. */
  setupPath?: string;
  /** Opens a frontend's environment variables (the page's drawer). */
  onVariables?: (application: ProjectApplication) => void;
}

/** Why Deploy all cannot run, or null when it can. */
function deployAllBlockedReason(canDeploy: boolean, enabledCount: number, loaded: boolean): string | null {
  if (!canDeploy) return 'Set up this environment on a server first.';
  if (loaded && enabledCount === 0) return 'No component is enabled and placed on a server in this environment.';
  return null;
}

/** endWithStop keeps a machine error readable inside a sentence. */
function endWithStop(text?: string): string {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return 'The teardown did not finish.';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function shortRepo(url: string): string {
  return url.replace(/^(https?:\/\/|git@)/, '').replace(/^github\.com[:/]/, '').replace(/\.git$/, '');
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * ComponentsCard is the environment's component picture: the shared
 * Magento backend and every storefront frontend, each with its own
 * repository, hostnames, Magento website, release and health — and each
 * deployable, inspectable and configurable on its own. Deploy all runs
 * them as a coordinated deployment that keeps every outcome separate.
 */
export function ComponentsCard({
  projectId,
  environment,
  canDeploy,
  reloadToken = 0,
  onDeploy,
  onOpenDeployment,
  onOpenGroup,
  onApplicationsChanged,
  addFrontendBlockedReason = null,
  onConfigureAccess,
  onAddFrontend,
  headless = false,
  setupPath,
  onVariables,
}: ComponentsCardProps) {
  const overview = useResource<ComponentsOverview>(
    () => api.getComponentsOverview(projectId, environment.id),
    [projectId, environment.id, reloadToken],
    undefined,
    { key: `project:${projectId}:env:${environment.id}:components-card` },
  );
  const groups = useResource<{ groups: DeploymentGroup[] }>(
    () => api.listDeploymentGroups(environment.id).catch(() => ({ groups: [] })),
    [environment.id, reloadToken],
    undefined,
    { key: `project:${projectId}:env:${environment.id}:deployment-groups` },
  );
  const [adding, setAdding] = useState(false);
  const [settingsFor, setSettingsFor] = useState<ProjectApplication | null>(null);
  const [historyFor, setHistoryFor] = useState<ComponentView | null>(null);
  const [removing, setRemoving] = useState<ProjectApplication | null>(null);
  const [removeError, setRemoveError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const [purgeNotice, setPurgeNotice] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showOlderGroups, setShowOlderGroups] = useState(false);
  const frameworks = useFrontendFrameworks();

  // A component in flight keeps the picture fresh without a page reload.
  const removingNow = overview.data?.components?.some((c) => c.lifecycle === 'removing') ?? false;
  const deploying = (overview.data?.components?.some((c) => c.deploying) ?? false) || removingNow;
  const runningGroup = groups.data?.groups?.some((g) => g.status === 'running' || g.status === 'pending') ?? false;
  useEffect(() => {
    if (!deploying && !runningGroup) return;
    const t = setInterval(() => {
      overview.reload();
      groups.reload();
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploying, runningGroup]);

  const refresh = useCallback(() => {
    overview.reload();
    groups.reload();
    onApplicationsChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onApplicationsChanged]);

  async function remove() {
    if (!removing) return;
    setBusy(true);
    setRemoveError(null);
    try {
      await api.removeComponent(projectId, removing.id);
      setRemoving(null);
      refresh();
    } catch (err) {
      setRemoveError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  async function retryRemoval(app: ProjectApplication) {
    setBusy(true);
    try {
      await api.retryComponentRemoval(projectId, app.id);
      refresh();
    } catch (err) {
      setRemoveError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  async function purgeCache(app: ProjectApplication) {
    setPurgeNotice(null);
    setRemoveError(null);
    try {
      const res = await api.purgeComponentCache(projectId, app.id, environment.id);
      // A purge the server skipped is not shown as done.
      setPurgeNotice(
        res.skipped
          ? `${app.name}: nothing was purged${res.reason ? ` (${res.reason})` : ''}.`
          : `${app.name}: invalidated ${res.hostnames.join(', ')} through ${cacheProviderLabel[res.provider] ?? res.provider}${res.attempts && res.attempts > 1 ? ` after ${res.attempts} attempts` : ''}.`,
      );
    } catch (err) {
      setRemoveError(err instanceof Error ? err : new Error(String(err)));
    }
  }

  const components = overview.data?.components ?? [];
  const backend = components.find((c) => c.application.type === 'magento_backend');
  const frontends = components.filter((c) => c.application.type === 'frontend');
  const enabledIds = components.filter((c) => !c.application.disabled && c.placed).map((c) => c.application.id);
  const allGroups = (groups.data?.groups ?? []).slice(0, 5);
  const recentGroups = showOlderGroups ? allGroups : allGroups.slice(0, 2);

  const loaded = Boolean(overview.data);
  // An empty state replaces the section's Add frontend rather than repeating it.
  const showEmpty = headless && loaded && frontends.length === 0;
  const deployAllReason = deployAllBlockedReason(canDeploy, enabledIds.length, loaded);
  const addFrontendButton = (
    <button
      type="button"
      className="button button--secondary"
      onClick={() => (onAddFrontend ? onAddFrontend() : setAdding(true))}
      disabled={Boolean(addFrontendBlockedReason)}
      title={addFrontendBlockedReason ?? undefined}
      aria-describedby={addFrontendBlockedReason ? 'add-frontend-blocked' : undefined}
      data-testid="add-frontend"
    >
      Add frontend
    </button>
  );
  const addFrontendReason = addFrontendBlockedReason ? (
    <p className="repos-reason" id="add-frontend-blocked" data-testid="add-frontend-blocked">
      {addFrontendBlockedReason}
      {setupPath ? (
        <>
          {' '}
          <Link to={setupPath}>Go to the setup</Link>
        </>
      ) : null}
    </p>
  ) : null;

  const columns = componentColumns({
    canDeploy,
    frameworkLabel: (value) => frameworks.find((f) => f.framework === value)?.label ?? (value || 'Custom'),
    open: openId,
    onToggle: (id) => setOpenId((current) => (current === id ? null : id)),
    onOpenDeployment,
  });

  return (
    <Section
      title={`Components in ${environment.name} (${components.length})`}
      actions={
        <>
          {showEmpty ? null : addFrontendButton}
          <button
            type="button"
            className="button button--secondary"
            disabled={Boolean(deployAllReason)}
            onClick={() => onDeploy(enabledIds)}
            data-testid="deploy-all"
            title={deployAllReason ?? undefined}
            aria-describedby={deployAllReason ? 'deploy-all-blocked' : undefined}
          >
            Deploy all
          </button>
        </>
      }
      testId="components-card"
      className="components repos-components"
    >
      {(addFrontendReason && !showEmpty) || deployAllReason ? (
        <div className="repos-reasons">
          {showEmpty ? null : addFrontendReason}
          {deployAllReason ? (
            <p className="repos-reason" id="deploy-all-blocked" data-testid="deploy-all-blocked">
              Deploy all: {deployAllReason}
            </p>
          ) : null}
        </div>
      ) : null}
      <ErrorNotice error={overview.error} />
      <ErrorNotice error={removeError} />
      {purgeNotice ? (
        <Callout tone="success" testId="purge-notice">
          {purgeNotice}
        </Callout>
      ) : null}
      {overview.loading && !overview.data ? <LoadingRows label="Loading components" rows={3} /> : null}

      {overview.data && (overview.data.issues?.length ?? 0) > 0 ? (
        <Callout tone="warning" title="Needs a decision" testId="components-issues">
          <ul className="repos-issues">
            {(overview.data.issues ?? []).map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {components.length > 0 ? (
        <ResponsiveTable
          rows={components}
          rowKey={(c) => c.application.id}
          caption={`Components in ${environment.name}`}
          className="components-table"
          testId="components-table"
          columns={columns}
          actionsSize="lg"
          actions={(c) => (
            <ComponentActions
              component={c}
              canDeploy={canDeploy}
              busy={busy}
              onDeploy={() => onDeploy([c.application.id])}
              onHistory={() => setHistoryFor(c)}
              onLogs={c.last_deployment ? () => onOpenDeployment(c.last_deployment!.id) : undefined}
              onBuildSettings={() => setSettingsFor(c.application)}
              onVariables={onVariables && c.application.type === 'frontend' ? () => onVariables(c.application) : undefined}
              onRemove={c.application.type === 'frontend' ? () => { setRemoveError(null); setRemoving(c.application); } : undefined}
              onRetryRemoval={c.lifecycle === 'removal_failed' ? () => retryRemoval(c.application) : undefined}
              onPurgeCache={c.cache?.provider && c.cache.provider !== 'none' ? () => purgeCache(c.application) : undefined}
            />
          )}
          detail={(c) => (openId === c.application.id || needsAttention(c) ? <ComponentDetail component={c} open={openId === c.application.id} projectId={projectId} environmentId={environment.id} onOpenDeployment={onOpenDeployment} /> : null)}
          rowProps={(c) => ({ testId: `component-${c.application.slug}` })}
        />
      ) : null}

      {showEmpty ? (
        <div className="repos-empty" data-testid="no-frontends">
          <p className="quiet-empty">No frontends</p>
          <div className="empty-state__actions">{addFrontendButton}</div>
          {addFrontendReason}
        </div>
      ) : null}

      {backend && frontends.length > 0 ? (
        <details className="quiet-disclosure components__relationships" data-testid="component-relationships">
          <summary>Component relationships</summary>
          <ArchitectureRail backend={backend} frontends={frontends} websites={overview.data?.websites ?? []} />
        </details>
      ) : null}

      {allGroups.length > 0 ? (
        <Subsection title="Coordinated deployments" testId="deployment-groups">
          <ul className="components__group-list">
            {recentGroups.map((g) => {
              // An older API sent this list as "order"; a missing list is
              // counted as unknown rather than taking the page down.
              const count = g.deployment_order?.length;
              return (
              <li key={g.id} className="components__group" data-testid={`group-${g.id}`}>
                <StatusBadge kind="deploymentGroup" value={g.status} label={groupHistoryLabel(g)} />
                {groupNowValue(g) ? <StatusBadge kind="groupCurrent" value={groupNowValue(g)!} /> : null}
                <span>
                  {g.scope === 'all' ? 'Deploy all' : 'Deploy selected'}
                  {count !== undefined ? ` · ${count} ${count === 1 ? 'component' : 'components'}` : ''} · by {g.created_by} · {relative(g.created_at)}
                </span>
                {g.failure_reason ? <span className="muted components__group-reason">{g.failure_reason}</span> : null}
                <button
                  type="button"
                  className="button button--secondary button--small"
                  onClick={() => onOpenGroup(g.id)}
                  aria-label={`View the coordinated deployment started ${relative(g.created_at)}`}
                >
                  View
                </button>
              </li>
              );
            })}
          </ul>
          {allGroups.length > 2 ? (
            <button
              type="button"
              className="button button--ghost button--small components__groups-toggle"
              aria-expanded={showOlderGroups}
              onClick={() => setShowOlderGroups((value) => !value)}
              data-testid="deployment-groups-toggle"
            >
              {showOlderGroups ? 'Show fewer' : `Show ${allGroups.length - 2} older`}
            </button>
          ) : null}
        </Subsection>
      ) : null}

      {adding ? (
        <AddFrontendModal
          projectId={projectId}
          environment={environment}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            refresh();
          }}
          onConfigureAccess={
            onConfigureAccess
              ? () => {
                  setAdding(false);
                  onConfigureAccess();
                }
              : undefined
          }
        />
      ) : null}
      {settingsFor ? (
        <ComponentSettingsModal
          projectId={projectId}
          application={settingsFor}
          others={components.map((c) => c.application).filter((a) => a.id !== settingsFor.id)}
          environmentName={environment.name}
          environmentId={environment.id}
          onEditVariables={
            onVariables
              ? () => {
                  const application = settingsFor;
                  setSettingsFor(null);
                  onVariables(application);
                }
              : undefined
          }
          onClose={() => setSettingsFor(null)}
          onSaved={() => {
            setSettingsFor(null);
            refresh();
          }}
        />
      ) : null}
      {historyFor ? (
        <ComponentHistoryPanel
          projectId={projectId}
          environment={environment}
          component={historyFor}
          onClose={() => setHistoryFor(null)}
          onOpenDeployment={(id) => {
            setHistoryFor(null);
            onOpenDeployment(id);
          }}
          onRedeploy={() => {
            const id = historyFor.application.id;
            setHistoryFor(null);
            onDeploy([id]);
          }}
        />
      ) : null}
      <ConfirmDialog
        open={removing !== null}
        title={removing ? `Remove ${removing.name}?` : 'Remove component'}
        description={
          removing
            ? `Commerce Ops will stop the ${removing.name} container, remove its service from the environment, take its hostnames out of the gate, and delete its variables, secrets and monitors. Magento and the other storefronts keep running and keep their routing. Deployment and audit history is preserved, and the Git repository is never touched. If the teardown fails, the component stays here as "Removal failed" so it can be retried.`
            : ''
        }
        confirmLabel="Stop and remove"
        confirmText={removing?.name}
        danger
        busy={busy}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      >
        <ErrorNotice error={removeError} />
      </ConfirmDialog>
    </Section>
  );
}

/** The wiring: one backend, its websites, the storefront that serves each. */
function ArchitectureRail({ backend, frontends, websites }: { backend: ComponentView; frontends: ComponentView[]; websites: string[] }) {
  const byWebsite = new Map<string, ComponentView[]>();
  for (const fe of frontends) {
    const key = fe.website_code ?? '';
    byWebsite.set(key, [...(byWebsite.get(key) ?? []), fe]);
  }
  const rows: { website: string; frontends: ComponentView[] }[] = [];
  for (const w of websites) {
    rows.push({ website: w, frontends: byWebsite.get(w) ?? [] });
    byWebsite.delete(w);
  }
  for (const [w, fes] of byWebsite) {
    rows.push({ website: w, frontends: fes });
  }
  return (
    <div className="rail" role="img" aria-label={`${backend.application.name} serves ${websites.length || 'its'} ${websites.length === 1 ? 'website' : 'websites'}; ${frontends.length} ${frontends.length === 1 ? 'storefront' : 'storefronts'} in front of it`} data-testid="architecture-rail">
      <div className="rail__backend">
        <span className="rail__node rail__node--backend">{backend.application.name}</span>
        <span className="muted rail__hint">shared backend</span>
      </div>
      <ol className="rail__branches">
        {rows.length === 0 ? (
          <li className="rail__branch">
            <span className="muted">No websites discovered yet — run a topology discovery on the environment.</span>
          </li>
        ) : (
          rows.map((row) => (
            <li key={row.website || 'unbound'} className="rail__branch">
              <span className="rail__node rail__node--website">
                <code>{row.website || 'no website'}</code>
              </span>
              {row.frontends.length === 0 ? (
                <span className="muted rail__hint">served by Magento itself</span>
              ) : (
                row.frontends.map((fe) => (
                  <span key={fe.application.id} className="rail__frontend">
                    <span className={`rail__node rail__node--frontend ${fe.application.disabled ? 'rail__node--off' : ''}`}>{fe.application.name}</span>
                    {fe.domains.filter((d) => d.canonical).map((d) => (
                      <code key={d.id} className="rail__host">{d.hostname}</code>
                    ))}
                  </span>
                ))
              )}
            </li>
          ))
        )}
      </ol>
    </div>
  );
}

/** Why one component cannot deploy, beyond the environment-wide reason. */
function ownDeployReason(c: ComponentView): string | null {
  const app = c.application;
  if (!c.placed) return `${app.name} is not placed on a server in this environment.`;
  if (app.disabled) return `${app.name} is disabled; enable it in its build settings.`;
  if (c.deploying) return `${app.name} is deploying now.`;
  if (c.lifecycle === 'removing') return `${app.name} is being removed.`;
  if (c.lifecycle === 'removal_failed') return 'The removal failed; retry it before deploying again.';
  return null;
}

/** "owner/name", free to break after the owner when the column is narrow. */
function RepositoryName({ url }: { url: string }) {
  const short = shortRepo(url);
  const slash = short.indexOf('/');
  return (
    <code title={url} className="components-table__repo">
      {slash > 0 ? (
        <>
          {short.slice(0, slash + 1)}
          <wbr />
          {short.slice(slash + 1)}
        </>
      ) : (
        short
      )}
    </code>
  );
}

/** "php83" → "PHP 8.3", "node22" → "Node.js 22". */
function runtimeLabel(runtime: string | undefined): string {
  const value = (runtime ?? '').trim().toLowerCase();
  const php = value.match(/^php(\d)(\d+)$/);
  if (php) return `PHP ${php[1]}.${php[2]}`;
  const node = value.match(/^node(\d+)/);
  if (node) return `Node.js ${node[1]}`;
  return runtime ?? '';
}

/**
 * The component table's columns (CO-159): the component, where its code
 * comes from, how it is built, what is live and its state. Everything else
 * — server, website binding, hostnames, cache, the last deployment — is one
 * click away in the row's details.
 */
function componentColumns({
  canDeploy,
  frameworkLabel,
  open,
  onToggle,
  onOpenDeployment,
}: {
  canDeploy: boolean;
  frameworkLabel: (framework: string) => string;
  open: string | null;
  onToggle: (applicationId: string) => void;
  onOpenDeployment: (deploymentId: string) => void;
}): Column<ComponentView>[] {
  return [
    {
      id: 'component',
      header: 'Component',
      card: 'title',
      width: 'fill',
      cell: (c) => {
        const app = c.application;
        const expanded = open === app.id;
        return (
          <span className="component-cell">
            <button
              type="button"
              className="component-cell__toggle"
              aria-expanded={expanded}
              aria-controls={expanded ? `component-detail-${app.id}` : undefined}
              aria-label={`${expanded ? 'Hide' : 'Show'} details of ${app.name}`}
              onClick={() => onToggle(app.id)}
              data-testid={`component-toggle-${app.slug}`}
            >
              <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={16} />
            </button>
            <span className="cell-stack">
              <span className="cell-stack__primary">{app.name}</span>
              <span className="cell-stack__secondary">
                {applicationTypeLabel[app.type] ?? humanize(app.type)}
                {app.disabled ? ' · disabled' : ''}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      id: 'repository',
      header: 'Repository and branch',
      cardLabel: 'Repository',
      width: 20,
      cell: ({ application: app }) => (
        <span className="cell-stack">
          {app.repository_url ? <RepositoryName url={app.repository_url} /> : <span className="muted">Not set</span>}
          <span className="cell-stack__secondary">
            {app.default_branch ? (
              <>
                on <code>{app.default_branch}</code>
              </>
            ) : (
              'Branch not set'
            )}
          </span>
        </span>
      ),
    },
    {
      id: 'build',
      header: 'Build and runtime',
      cardLabel: 'Build',
      width: 12,
      priority: 'secondary',
      cell: ({ application: app }) =>
        app.type === 'frontend' ? (
          <span className="cell-stack">
            <span>{frameworkLabel(app.framework ?? '')}</span>
            <span className="cell-stack__secondary">
              {[app.package_manager, runtimeLabel(app.runtime)].filter(Boolean).join(' · ')}
            </span>
          </span>
        ) : (
          <span>{runtimeLabel(app.runtime) || <span className="muted">Not known</span>}</span>
        ),
    },
    {
      id: 'live',
      header: 'Live release',
      width: 12,
      cell: (c) =>
        c.active_release ? (
          <span className="cell-stack">
            <code>{c.active_release.commit_sha.slice(0, 7)}</code>
            {c.active_release.activated_at ? <span className="cell-stack__secondary">{relative(c.active_release.activated_at)}</span> : null}
          </span>
        ) : (
          <span className="muted">Nothing deployed yet</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      card: 'status',
      width: 'status',
      cell: (c) => {
        const reason = ownDeployReason(c);
        const last = c.last_deployment;
        return (
          <span className="cell-stack" data-testid="component-health">
            {c.lifecycle && c.lifecycle !== 'active' ? (
              <StatusBadge kind="componentLifecycle" value={c.lifecycle} />
            ) : (
              <StatusBadge kind="componentHealth" value={c.health} />
            )}
            {c.deploying ? <span className="cell-stack__secondary">Deploying now</span> : null}
            {!c.deploying && last?.status === 'failed' ? (
              <span className="cell-stack__secondary">
                Last deployment failed {relative(last.created_at)}{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => onOpenDeployment(last!.id)}
                  aria-label={`View error of the last ${c.application.name} deployment`}
                  data-testid="component-view-error"
                >
                  View error
                </button>
              </span>
            ) : null}
            {/* Why its Deploy is unavailable; the environment-wide reason is said once, above. */}
            {canDeploy && reason && !c.deploying && c.lifecycle !== 'removal_failed' && c.lifecycle !== 'removing' ? (
              <span className="cell-stack__secondary" data-testid="component-deploy-reason">
                {reason}
              </span>
            ) : null}
          </span>
        );
      },
    },
  ];
}

/** A component's actions: its main one as a button, the rest in a menu, Remove last. */
function ComponentActions({
  component: c,
  canDeploy,
  busy,
  onDeploy,
  onHistory,
  onLogs,
  onBuildSettings,
  onVariables,
  onRemove,
  onRetryRemoval,
  onPurgeCache,
}: {
  component: ComponentView;
  canDeploy: boolean;
  busy: boolean;
  onDeploy: () => void;
  onHistory: () => void;
  onLogs?: () => void;
  onBuildSettings: () => void;
  onVariables?: () => void;
  onRemove?: () => void;
  onRetryRemoval?: () => void;
  onPurgeCache?: () => void;
}) {
  const app = c.application;
  const isFrontend = app.type === 'frontend';
  const deployReason = !canDeploy ? 'Set up this environment on a server first.' : ownDeployReason(c);
  const deployAction: MenuAction = {
    label: c.deploying ? 'Deploying' : 'Deploy',
    onClick: onDeploy,
    disabled: deployReason !== null,
    testId: 'component-deploy',
  };
  const main = onRetryRemoval ? (
    <button type="button" className="button button--secondary button--small" onClick={onRetryRemoval} disabled={busy} data-testid="component-retry-removal">
      Retry removal
    </button>
  ) : isFrontend ? (
    <button type="button" className="button button--secondary button--small" onClick={onBuildSettings} data-testid={`build-settings-${app.slug}`}>
      Edit build settings
    </button>
  ) : (
    <button
      type="button"
      className="button button--secondary button--small"
      onClick={onDeploy}
      disabled={deployReason !== null}
      title={deployReason ?? undefined}
      data-testid="component-deploy"
    >
      {c.deploying ? 'Deploying' : 'Deploy'}
    </button>
  );
  const menu: MenuAction[] = [];
  if (isFrontend) menu.push(deployAction);
  if (isFrontend && onRetryRemoval) menu.push({ label: 'Edit build settings', onClick: onBuildSettings, testId: `build-settings-${app.slug}` });
  if (onVariables) menu.push({ label: 'Variables', onClick: onVariables, testId: `variables-${app.slug}` });
  menu.push({ label: 'History', onClick: onHistory, testId: 'component-history' });
  if (onLogs) menu.push({ label: 'Logs of the last deployment', onClick: onLogs, testId: 'component-logs' });
  if (app.type === 'magento_backend') menu.push({ label: 'Cache settings', onClick: onBuildSettings, testId: `cache-settings-${app.slug}` });
  if (onPurgeCache) menu.push({ label: 'Purge cache', onClick: onPurgeCache, testId: 'component-purge' });
  if (onRemove && !onRetryRemoval) menu.push({ label: 'Remove', onClick: onRemove, danger: true, disabled: c.lifecycle === 'removing', testId: 'component-remove' });
  return (
    <>
      {main}
      <ActionMenu label={`More actions for ${app.name}`} items={menu} size="sm" testId="component-more" />
    </>
  );
}

/** A component whose details are shown without being opened: what needs a person. */
function needsAttention(c: ComponentView): boolean {
  return c.lifecycle === 'removal_failed' || c.lifecycle === 'removing' || c.issues.some((i) => !i.startsWith('removal failed:'));
}

/**
 * A component's details under its row: what needs attention always, the
 * rest of its facts when opened.
 */
function ComponentDetail({ component: c, open, projectId, environmentId, onOpenDeployment }: { component: ComponentView; open: boolean; projectId: string; environmentId: string; onOpenDeployment: (deploymentId: string) => void }) {
  const app = c.application;
  const isFrontend = app.type === 'frontend';
  const last: ProjectDeployment | null | undefined = c.last_deployment;
  // A failed removal has its own notice; the matching issue is not repeated.
  const issues = c.issues.filter((i) => !i.startsWith('removal failed:'));
  const removalFailed = c.lifecycle === 'removal_failed';
  const removingNow = c.lifecycle === 'removing';
  return (
    <div className="component-detail" id={`component-detail-${app.id}`} data-testid={`component-detail-${app.slug}`}>
      {issues.length > 0 ? (
        <ul className="ctile__issues" data-testid="component-issues">
          {issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      ) : null}
      {removalFailed ? (
        <Callout tone="danger" title="Removal failed." testId="removal-failed">
          {endWithStop(c.removal_error)} This storefront may still be running and serving traffic; retry the removal.
        </Callout>
      ) : null}
      {removingNow ? <Callout tone="info">Stopping this storefront and removing it from the environment…</Callout> : null}
      {open ? (
        <dl className="component-detail__facts">
          <div>
            <dt>Runs on</dt>
            <dd>{c.placed ? c.server_name || 'Its server' : <span className="muted">Not placed in this environment</span>}</dd>
          </div>
          {isFrontend ? (
            <div>
              <dt>Magento website</dt>
              <dd>
                {c.website_code ? (
                  <>
                    <code>{c.website_code}</code>
                    {c.store_view_code ? <span className="muted"> · store view <code>{c.store_view_code}</code></span> : null}
                  </>
                ) : (
                  <span className="muted">Not bound</span>
                )}
              </dd>
            </div>
          ) : c.dependents.length > 0 ? (
            <div>
              <dt>Serves</dt>
              <dd>{c.dependents.join(', ')}</dd>
            </div>
          ) : null}
          <div>
            <dt>Hostnames</dt>
            <dd>
              {c.domains.length === 0 ? (
                <span className="muted">None in this environment</span>
              ) : (
                <ul className="ctile__hosts">
                  {c.domains.map((d) => (
                    <li key={d.id}>
                      <code>{d.hostname}</code>
                      {!d.canonical ? <span className="muted"> alias</span> : null}
                      <StatusBadge
                        kind="tls"
                        value={d.tls_observed_status}
                        title={`DNS: ${dnsStatusLabel[d.dns_status] ?? d.dns_status}`}
                        className="ctile__host-status"
                      />
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
          {isFrontend || app.type === 'magento_backend' ? (
            <div>
              <dt>Cache</dt>
              <dd data-testid="component-cache">
                {c.cache?.provider && c.cache.provider !== 'none' ? (
                  <>
                    {cacheProviderLabel[c.cache.provider] ?? c.cache.provider}
                    <span className="muted">
                      {c.cache_plan?.purge ? ` · invalidates ${c.cache_plan.targets.map((t) => t.hostname).join(', ')}` : ' · automatic purge off'}
                      {c.cache_plan?.outside_zone?.length ? ` · outside the zone, not purged: ${c.cache_plan.outside_zone.join(', ')}` : ''}
                      {c.cache?.warmup_enabled ? ` · warms ${c.cache_plan?.warmup_urls?.length ?? 0} URL(s)` : ''}
                    </span>
                  </>
                ) : (
                  <span className="muted">Not configured: releases go live without an external purge</span>
                )}
                {c.cache?.provider === 'cloudflare' ? <CloudflareFacts projectId={projectId} environmentId={environmentId} applicationId={app.id} /> : null}
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Last deployment</dt>
            <dd>
              {last ? (
                <>
                  <StatusBadge kind="deployment" value={last.status} />
                  <span className="muted"> {relative(last.created_at)}</span>
                  {last.status === 'failed' && last.failure_reason ? <span className="muted ctile__reason">{last.failure_reason}</span> : null}
                  {last.status === 'failed' ? (
                    <button type="button" className="link-button" onClick={() => onOpenDeployment(last.id)}>
                      View error
                    </button>
                  ) : null}
                </>
              ) : (
                <span className="muted">None</span>
              )}
            </dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}

const sslModeLabel: Record<string, string> = { off: 'Off', flexible: 'Flexible', full: 'Full', strict: 'Full (strict)' };

/**
 * What Cloudflare says about the storefront's own hostnames, read with its
 * cache token (CO-192): the zone, its SSL mode, and whether each hostname is
 * proxied. It only reads; purging is the component's Purge cache action.
 * A part the token may not read is said so, never filled in.
 */
function CloudflareFacts({ projectId, environmentId, applicationId }: { projectId: string; environmentId: string; applicationId: string }) {
  const view = useResource(() => api.getComponentCloudflare(projectId, applicationId, environmentId), [projectId, applicationId, environmentId], undefined, {
    key: `project:${projectId}:env:${environmentId}:cloudflare:${applicationId}`,
  });
  if (view.error && !view.data) return <ErrorNotice error={view.error} />;
  if (!view.data) return <span className="muted ctile__reason">Reading Cloudflare</span>;
  // Every list present, so an older or partial answer never breaks the page.
  const data = { ...view.data, hostnames: view.data.hostnames ?? [] };
  const unreadable = Object.entries(data.unreadable ?? {});
  return (
    <div className="cloudflare-facts" data-testid="component-cloudflare">
      <span className="cell-inline">
        {data.zone ? (
          <>
            <code translate="no">{data.zone.name}</code>
            <span className="muted">{data.zone.paused ? 'Paused' : humanize(data.zone.status)}</span>
          </>
        ) : null}
        {data.ssl_mode ? <span className="muted">SSL {sslModeLabel[data.ssl_mode] ?? data.ssl_mode}</span> : null}
      </span>
      <ul className="ctile__hosts">
        {data.hostnames.map((h) => (
          <li key={h.hostname}>
            <code translate="no">{h.hostname}</code>{' '}
            <span className="muted">
              {h.covered === false ? 'Outside zone · not purged' : h.proxied === undefined ? (data.unreadable?.dns_records ? 'Not readable' : 'No DNS record') : h.proxied ? 'Proxied' : 'DNS only'}
            </span>
          </li>
        ))}
      </ul>
      {unreadable.length > 0 ? (
        <span className="muted ctile__reason" data-testid="cloudflare-unreadable">
          {unreadable.map(([part, reason]) => `${humanize(part)}: ${reason}`).join(' · ')}
        </span>
      ) : null}
    </div>
  );
}
